import React, { useState, useEffect, useRef } from 'react'
import UserAvatar from '../shared/UserAvatar'
import { buildTeamShareUrl, formatCurrency } from 'utils'
import { usePromotionCopy, useDiscountPreviews } from 'hooks'
import PromotionLinkModal from './PromotionLinkModal'
import TeacherProfileContent from './TeacherProfileContent'
import { formatSchedulePattern } from 'utils/scheduleUtils'
import { trackEvent } from 'utils/analytics'
import { rememberPreferredTeacher } from 'utils/preferredTeacher'

// On a phone the faculty is a vertical list. It shows this many teachers
// before the visitor asks for the rest.
const MOBILE_VISIBLE_COUNT = 5

export default function TeamMembersSection({ members, colors, courses, groupClasses, onPurchase, onClaimTrial, onOpenGroupClassDetail, trialUsed, translations, teamSlug, initialMemberId, onConsult, consultationEnabled, currentUser, teamId }) {
  const [expandedMember, setExpandedMember] = useState(null)
  const [expandedCourseId, setExpandedCourseId] = useState(null)
  const { handleCopyClick, modalState, closeModal: closePromoModal, isCopied, inviteCode } = usePromotionCopy({ currentUser, teamId })
  const listRef = useRef(null)
  const focusSixthRowRef = useRef(false)
  const sectionRef = useRef(null)
  const expandedCourseRef = useRef(null)

  // Exactly one visible member → render the profile inline instead of cards/modal.
  const isSingleTeacher = members.length === 1
  const soloMember = isSingleTeacher ? members[0] : null
  const [showAllMembers, setShowAllMembers] = useState(false)
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768)

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // After "show the rest", keyboard focus moves to the first newly shown row.
  useEffect(() => {
    if (!showAllMembers || !focusSixthRowRef.current) return
    focusSixthRowRef.current = false
    listRef.current?.querySelectorAll('[data-member-row]')[MOBILE_VISIBLE_COUNT]?.focus()
  }, [showAllMembers])

  // Deep link from ?member=: multi-teacher opens the matching modal; single-teacher
  // has no modal, so scroll to the (already-visible) inline profile instead.
  useEffect(() => {
    if (!initialMemberId || !members?.length) return
    if (isSingleTeacher) {
      sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      return
    }
    const member = members.find(m => m.id === initialMemberId)
    if (member) setExpandedMember(member)
  }, [initialMemberId, isSingleTeacher, members])

  // Lock body scroll when member modal is open
  useEffect(() => {
    if (!expandedMember) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const handleEsc = (e) => { if (e.key === 'Escape') setExpandedMember(null) }
    document.addEventListener('keydown', handleEsc)
    return () => {
      document.body.style.overflow = prev
      document.removeEventListener('keydown', handleEsc)
    }
  }, [expandedMember])

  // Reset accordion when switching teachers
  useEffect(() => {
    setExpandedCourseId(null)
  }, [expandedMember?.id])

  // Scroll expanded course into view
  useEffect(() => {
    if (expandedCourseId && expandedCourseRef.current) {
      expandedCourseRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [expandedCourseId])

  const handleMemberClick = (member) => {
    if (expandedMember?.id !== member.id) {
      trackEvent('teacher_profile_click', {
        teacher_name: member.name,
        team_slug: teamSlug,
        position: members.findIndex(m => m.id === member.id) + 1
      })
    }
    setExpandedMember(expandedMember?.id === member.id ? null : member)
  }

  const closeModal = () => {
    setExpandedMember(null)
  }

  const showRemainingMembers = () => {
    trackEvent('members_show_more', { hidden_count: members.length - MOBILE_VISIBLE_COUNT })
    focusSixthRowRef.current = true
    setShowAllMembers(true)
  }

  const toggleCourse = (courseId, hasPackages) => {
    if (!hasPackages) return
    setExpandedCourseId(prev => prev === courseId ? null : courseId)
  }

  // Get assigned courses for the expanded member (only those with packages)
  const memberCourses = expandedMember && courses
    ? courses.filter(c => expandedMember.assignedCourseIds?.includes(c.id) && c.packages?.length > 0)
      .sort((a, b) => {
        const minPrice = (c) => Math.min(...(c.packages || []).filter(p => !p.isTrial).map(p => p.unitPrice ?? p.totalPrice ?? Infinity))
        return minPrice(a) - minPrice(b)
      })
    : []

  // Student coupon / referral discount for the packages shown in the member modal
  // (shared team-wide batch, keyed by package id).
  const discountPreviews = useDiscountPreviews(teamId, !!currentUser?.id)

  // Get group classes for the expanded member
  const memberGroupClasses = expandedMember && groupClasses
    ? groupClasses.filter(gc => gc.teacher_id === expandedMember.userId)
    : []

  // Group class list injected between the profile fields and the consultation CTA
  // inside the modal (via TeacherProfileContent's beforeConsultation slot), preserving
  // the original modal field order.
  const groupClassesBlock = memberGroupClasses.length > 0 ? (
    <div className="mt-6 text-left">
      <h4 className="text-sm font-semibold text-gray-700 mb-3">{translations?.groupClassesTitle || 'Group Classes'}</h4>
      <div className="space-y-2">
        {memberGroupClasses.map((gc) => {
          const schedule = formatSchedulePattern(gc.schedule_rule) || (gc.sessions?.[0] ? new Date(gc.sessions[0].start_time).toLocaleDateString() : null)
          return (
            <div
              key={gc.id}
              role="button"
              tabIndex={0}
              onClick={() => onOpenGroupClassDetail?.(gc)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenGroupClassDetail?.(gc) } }}
              className="flex items-center justify-between px-4 py-3 rounded-xl border border-gray-200 cursor-pointer hover:border-gray-300 transition-all"
            >
              <div>
                <div className="flex items-center gap-2">
                  <h5 className="font-semibold text-gray-800 text-sm">{gc.title}</h5>
                  <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-blue-50 text-blue-600">
                    {translations?.groupClassBadge || 'Group'}
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs font-medium" style={{ color: colors.primary || '#3366FF' }}>
                    {formatCurrency(gc.price, gc.currency || 'TWD')}
                  </span>
                  {schedule && (
                    <>
                      <span className="text-xs text-gray-300">·</span>
                      <span className="text-xs text-gray-500">{schedule}</span>
                    </>
                  )}
                </div>
              </div>
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          )
        })}
      </div>
    </div>
  ) : null

  // Fired by the consultation CTA (rendered by TeacherProfileContent) in modal mode.
  const handleModalConsult = (m) => {
    setExpandedMember(null)
    onConsult({ source: 'member_profile', courseContext: m.name, memberId: m.id })
  }

  // Single-teacher teams render the profile inline — no cards, carousel, or modal,
  // and no teacher_profile_click event (there is no click interaction).
  if (isSingleTeacher) {
    return (
      <section ref={sectionRef} className="py-8 border-t border-gray-200">
        <div className="mb-6">
          <h2 className="text-[22px] font-semibold" style={{ color: '#222222' }}>{translations?.aboutTeacher || 'About the Teacher'}</h2>
        </div>
        <div className="flex flex-col md:flex-row gap-6 md:gap-8 items-center md:items-start">
          <UserAvatar
            src={soloMember.avatarUrl}
            name={soloMember.name || 'Teacher'}
            size="2xl"
            fallbackColor={colors.primary}
            className="shrink-0"
          />
          <div className="flex-1 min-w-0 text-center md:text-left">
            <h3 className="text-xl font-bold mb-2" style={{ color: '#222222' }}>{soloMember.name || 'Teacher'}</h3>
            {/* No consultation CTA in single-teacher mode — the page hero already
                surfaces the team consultation entry point, so an in-section CTA is
                redundant when the whole page is about this one teacher. */}
            <TeacherProfileContent
              member={soloMember}
              colors={colors}
              translations={translations}
              variant="inline"
            />
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className="py-8 border-t border-gray-200">
      <div className="mb-6">
        <h2 className="text-[22px] font-semibold" style={{ color: '#222222' }}>{translations?.title || 'Faculty'}</h2>
        <p className="text-sm mt-1" style={{ color: '#717171' }}>{translations?.subtitle || 'Click to view teacher profiles'}</p>
      </div>
      {isMobile ? (
        /* Mobile: vertical list, first MOBILE_VISIBLE_COUNT teachers, the rest on request */
        <div ref={listRef}>
          <div className="flex flex-col">
            {(showAllMembers ? members : members.slice(0, MOBILE_VISIBLE_COUNT)).map((member) => (
              <MemberRow key={member.id} member={member} colors={colors} onClick={() => handleMemberClick(member)} />
            ))}
          </div>
          {!showAllMembers && members.length > MOBILE_VISIBLE_COUNT && (
            <button
              type="button"
              onClick={showRemainingMembers}
              className="mt-3 w-full h-11 rounded-lg border text-sm font-semibold focus:outline-none focus:ring-2"
              style={{ color: colors.primary || '#3366FF', borderColor: colors.primary || '#3366FF', '--tw-ring-color': colors.primary || '#3366FF' }}
            >
              {(translations?.showRemainingTeachers || 'Show %{count} more teachers').replace('%{count}', members.length - MOBILE_VISIBLE_COUNT)}
            </button>
          )}
        </div>
      ) : (
        /* Grid layout: tablet and desktop */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {members.map((member) => (
            <MemberCard key={member.id} member={member} colors={colors} translations={translations} onClick={() => handleMemberClick(member)} />
          ))}
        </div>
      )}

      {/* Member Detail Modal */}
      {expandedMember && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 lg:p-4"
          onClick={closeModal}
        >
          <div
            className="bg-white w-full h-full lg:h-auto lg:max-h-[90vh] lg:max-w-lg lg:mx-4 lg:rounded-xl lg:shadow-xl overflow-hidden flex flex-col"
            style={{ overscrollBehavior: 'contain' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header with gradient */}
            <div
              className="p-6 pb-16 relative"
              style={{
                background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
              }}
            >
              <div className="absolute top-4 right-4 flex items-center gap-2">
                <button
                  onClick={() => handleCopyClick(buildTeamShareUrl(teamSlug, { member: expandedMember.id }, inviteCode))}
                  className="text-white/80 hover:text-white transition-colors cursor-pointer"
                  title={translations?.shareTeacherLink || 'Copy teacher link'}
                >
                  {isCopied ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                  )}
                </button>
                <button
                  onClick={closeModal}
                  className="text-white/80 hover:text-white transition-colors cursor-pointer"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Avatar overlapping header */}
            <div className="flex justify-center -mt-12 relative z-10">
              <UserAvatar
                src={expandedMember.avatarUrl}
                name={expandedMember.name || 'Teacher'}
                size="xl"
                fallbackColor={colors.primary}
              />
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6 pt-4 text-center" style={{ overscrollBehavior: 'contain' }}>
              <div className="flex items-center justify-center gap-2 mb-1">
                <h3 className="text-xl font-bold" style={{ color: '#222222' }}>{expandedMember.name || 'Teacher'}</h3>
              </div>

              <TeacherProfileContent
                member={expandedMember}
                colors={colors}
                translations={translations}
                variant="modal"
                consultationEnabled={consultationEnabled}
                onConsultClick={onConsult ? handleModalConsult : undefined}
                beforeConsultation={groupClassesBlock}
              />

              {/* Assigned Courses — accordion mode */}
              {memberCourses.length > 0 && (
                <div className="mt-6 text-left">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">{translations?.coursesTaught || 'Courses Taught'}</h4>
                  <div className="space-y-2">
                    {memberCourses.map((course) => {
                      const pkgs = course.packages || []
                      const regularPkgs = pkgs.filter(p => !p.isTrial)
                      const trialPkg = !trialUsed ? pkgs.find(p => p.isTrial) : null
                      const hasPackages = regularPkgs.length > 0 || !!trialPkg
                      const isExpanded = expandedCourseId === course.id
                      const minPkg = regularPkgs.length > 0
                        ? regularPkgs.reduce((min, p) => p.unitPrice < min.unitPrice ? p : min, regularPkgs[0])
                        : trialPkg
                      const hasDiscount = pkgs.some(p => discountPreviews[p.id]?.applied > 0)

                      // Compute lowest effective unit price considering discounts
                      let lowestEffectivePrice = minPkg ? (minPkg.unitPrice ?? minPkg.totalPrice) : null
                      let lowestOriginalPrice = lowestEffectivePrice
                      let lowestCurrency = minPkg?.currency
                      let priceIsDiscounted = false
                      if (hasDiscount) {
                        let best = null
                        let bestOriginalUnit = null
                        regularPkgs.forEach(p => {
                          const dp = discountPreviews[p.id]
                          const originalUnit = p.unitPrice ?? p.totalPrice
                          const effectiveUnit = dp?.applied > 0
                            ? dp.final_amount / (p.lessonCount || 1)
                            : originalUnit
                          if (best === null || effectiveUnit < best) {
                            best = effectiveUnit
                            bestOriginalUnit = originalUnit
                            lowestCurrency = p.currency
                          }
                        })
                        if (best !== null && best < lowestEffectivePrice) {
                          lowestOriginalPrice = bestOriginalUnit
                          lowestEffectivePrice = Math.floor(best)
                          priceIsDiscounted = true
                        }
                      }

                      return (
                        <div key={course.id} ref={isExpanded ? expandedCourseRef : undefined} className="rounded-xl border border-gray-200 overflow-hidden">
                          {/* Summary header — click to expand packages */}
                          <div
                            role={hasPackages ? 'button' : undefined}
                            tabIndex={hasPackages ? 0 : undefined}
                            aria-expanded={hasPackages ? isExpanded : undefined}
                            onClick={() => toggleCourse(course.id, hasPackages)}
                            onKeyDown={hasPackages ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleCourse(course.id, hasPackages) } } : undefined}
                            className={`flex items-center justify-between px-4 py-3 transition-all ${
                              hasPackages ? 'cursor-pointer hover:bg-gray-50' : ''
                            } ${isExpanded ? 'bg-gray-50' : ''}`}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <h5 className="font-semibold text-gray-800 text-sm">{course.name}</h5>
                                <span className="text-xs text-gray-400 shrink-0">{course.durationMinutes} {translations?.min || 'min'}</span>
                              </div>
                              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                {minPkg && priceIsDiscounted ? (
                                  <span className="text-xs font-medium flex items-center gap-1.5">
                                    <span className="line-through text-gray-400">{(translations?.fromPrice || 'From %{price}').replace('%{price}', formatCurrency(lowestOriginalPrice, lowestCurrency))}</span>
                                    <span style={{ color: colors.primary || '#3366FF' }}>{(translations?.fromPrice || 'From %{price}').replace('%{price}', formatCurrency(lowestEffectivePrice, lowestCurrency))}</span>
                                  </span>
                                ) : minPkg ? (
                                  <span className="text-xs font-medium" style={{ color: colors.primary || '#3366FF' }}>
                                    {(translations?.fromPrice || 'From %{price}').replace('%{price}', formatCurrency(lowestEffectivePrice, lowestCurrency))}
                                  </span>
                                ) : null}
                                {trialPkg && (
                                  <span
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      rememberPreferredTeacher(expandedMember.userId, course.id)
                                      const opts = { membershipId: expandedMember.id }
                                      if (trialPkg.totalPrice === 0) {
                                        onClaimTrial?.(course, opts)
                                      } else {
                                        onPurchase(course, trialPkg, opts)
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        rememberPreferredTeacher(expandedMember.userId, course.id)
                                        const opts = { membershipId: expandedMember.id }
                                        if (trialPkg.totalPrice === 0) {
                                          onClaimTrial?.(course, opts)
                                        } else {
                                          onPurchase(course, trialPkg, opts)
                                        }
                                      }
                                    }}
                                    role="button"
                                    tabIndex={0}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold text-white cursor-pointer hover:opacity-90 transition-opacity"
                                    style={{ background: 'linear-gradient(135deg, #10B981, #34D399)' }}
                                  >
                                    {trialPkg.totalPrice === 0
                                      ? (translations?.free || 'Free')
                                      : formatCurrency(trialPkg.totalPrice, trialPkg.currency)
                                    }
                                    {' '}{translations?.trialBadge || 'Trial'}
                                  </span>
                                )}
                              </div>
                            </div>
                            {hasPackages && (
                              <svg className={`w-4 h-4 text-gray-400 transition-transform shrink-0 ml-2 ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            )}
                          </div>

                          {/* Accordion expanded: trial + regular packages */}
                          {isExpanded && hasPackages && (
                            <div className="px-3 pb-3 space-y-2">
                              {/* Trial package */}
                              {trialPkg && (() => {
                                const handleTrialPurchase = () => {
                                  rememberPreferredTeacher(expandedMember.userId, course.id)
                                  const opts = { membershipId: expandedMember.id }
                                  if (trialPkg.totalPrice === 0) {
                                    onClaimTrial?.(course, opts)
                                  } else {
                                    onPurchase(course, trialPkg, opts)
                                  }
                                }
                                return (
                                <div
                                  role="button"
                                  tabIndex={0}
                                  onClick={handleTrialPurchase}
                                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleTrialPurchase() } }}
                                  className="flex items-center justify-between px-4 py-3 rounded-lg cursor-pointer transition-all hover:opacity-90"
                                  style={{ background: 'linear-gradient(135deg, #10B981, #34D399)' }}
                                >
                                  <div>
                                    <span className="font-bold text-white text-base">
                                      {trialPkg.totalPrice === 0 ? (translations?.free || 'Free') : formatCurrency(trialPkg.totalPrice, trialPkg.currency)}
                                    </span>
                                    <span className="block text-emerald-100 text-xs">
                                      {trialPkg.durationMinutes || course.durationMinutes}{translations?.trialLesson || 'min trial lesson'}
                                    </span>
                                  </div>
                                  <svg className="w-4 h-4 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                </div>
                                )
                              })()}

                              {/* Regular packages */}
                              {regularPkgs.map((pkg, idx) => {
                                const dp = discountPreviews[pkg.id]
                                // Strike the actual order amount (total the coupon discounts), not unit price.
                                const origAmount = dp?.original_amount ?? pkg.totalPrice
                                const showStrikethrough = pkg.lessonCount === 1 && dp && dp.applied > 0 && dp.final_amount < origAmount
                                const baseUnitPrice = regularPkgs[0]?.unitPrice
                                const discountPercent = baseUnitPrice && idx > 0
                                  ? Math.round((1 - pkg.unitPrice / baseUnitPrice) * 100)
                                  : 0
                                const showPopular = regularPkgs.length > 1 && idx === (regularPkgs.length === 2 ? regularPkgs.length - 1 : regularPkgs.length - 2)
                                const savingsAmount = baseUnitPrice && idx > 0
                                  ? (baseUnitPrice * pkg.lessonCount) - pkg.totalPrice
                                  : 0

                                const handlePkgPurchase = () => {
                                  rememberPreferredTeacher(expandedMember.userId, course.id)
                                  onPurchase(course, pkg, { membershipId: expandedMember.id })
                                }

                                return (
                                  <div
                                    key={pkg.id}
                                    role="button"
                                    tabIndex={0}
                                    onClick={handlePkgPurchase}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handlePkgPurchase() } }}
                                    className="flex items-center justify-between px-4 py-3 rounded-lg cursor-pointer transition-all border-2 border-gray-200 hover:border-gray-300"
                                  >
                                    <div>
                                      {showStrikethrough ? (
                                        <span className="font-bold text-lg">
                                          <span className="line-through text-gray-400 mr-2">{formatCurrency(origAmount, pkg.currency)}</span>
                                          <span style={{ color: '#3366FF' }}>{formatCurrency(dp.final_amount, pkg.currency)}</span>
                                        </span>
                                      ) : (
                                        <span className="font-bold text-lg text-gray-900">
                                          {formatCurrency(pkg.unitPrice, pkg.currency)}
                                        </span>
                                      )}
                                      <span className="block text-sm text-gray-500">
                                        {pkg.lessonCount} {translations?.lessonCount || 'lesson(s)'} {pkg.durationMinutes || course.durationMinutes} {translations?.min || 'min'}
                                      </span>
                                      {savingsAmount > 0 && (
                                        <span className="block text-xs font-medium text-emerald-600 mt-0.5">
                                          {(translations?.savingsAmount || 'Save %{amount}').replace('%{amount}', formatCurrency(savingsAmount, pkg.currency))}
                                        </span>
                                      )}
                                      {discountPreviews[pkg.id]?.applied > 0 && (
                                        <span className="block text-xs font-semibold mt-0.5" style={{ color: '#3366FF' }}>
                                          🎁 {discountPreviews[pkg.id].welcome_applied > 0
                                            ? (translations?.welcomeCouponLabel || 'Welcome coupon')
                                            : (translations?.referralDiscountLabel || 'Referral discount')} {discountPreviews[pkg.id].final_amount <= 0
                                            ? (translations?.couponFree || 'First purchase free')
                                            : `−${formatCurrency(discountPreviews[pkg.id].applied, pkg.currency)}`}
                                        </span>
                                      )}
                                      {(showPopular || discountPercent > 0) && (
                                        <div className="flex items-center gap-1.5 mt-1.5">
                                          {showPopular && (
                                            <span
                                              className="px-2 py-0.5 text-xs font-semibold rounded-full text-white"
                                              style={{ backgroundColor: colors?.primary || '#3366FF' }}
                                            >
                                              {translations?.popularBadge || 'Popular'}
                                            </span>
                                          )}
                                          {discountPercent > 0 && (
                                            <span
                                              className="px-2 py-0.5 text-xs font-semibold rounded border"
                                              style={{ color: colors?.primary || '#3366FF', borderColor: colors?.primary || '#3366FF' }}
                                            >
                                              {(translations?.discountOff || '%{percent}% off').replace('%{percent}', discountPercent)}
                                            </span>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex items-center shrink-0 ml-3">
                                      <span className="text-gray-400">›</span>
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      )}
      <PromotionLinkModal
        isOpen={modalState.isOpen}
        onClose={closePromoModal}
        baseUrl={modalState.baseUrl}
        teamId={teamId}
        inviteCode={inviteCode}
        colors={colors}
        translations={translations?.promotionModal}
      />
    </section>
  )
}

function MemberRow({ member, colors, onClick }) {
  return (
    <div
      data-member-row
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick() } }}
      className="flex items-center gap-3 min-h-[64px] py-2.5 border-b border-gray-100 cursor-pointer hover:bg-gray-50 focus:outline-none focus:ring-2"
      style={{ '--tw-ring-color': colors.primary || '#3366FF' }}
    >
      <UserAvatar
        src={member.avatarUrl}
        name={member.name || 'Teacher'}
        size="md"
        fallbackColor={colors.primary}
        className="shrink-0"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="font-bold text-base truncate" style={{ color: '#222222' }}>{member.name || 'Teacher'}</span>
          {member.averageRating && (
            <span className="text-[13px] shrink-0" style={{ color: '#4b5563' }}>★ {member.averageRating}</span>
          )}
        </div>
        {member.headline && (
          <p className="text-[13px] truncate" style={{ color: '#717171' }}>{member.headline}</p>
        )}
      </div>
      <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </div>
  )
}

function MemberCard({ member, colors, translations, onClick }) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick() } }}
      className="group border border-gray-100 rounded-xl p-6 hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer"
    >
      <div className="flex flex-col items-center">
        <div className="relative mb-4">
          {/* Was "2xl" (128px). The card no longer carries the bio, and a
              128px disc left the name and tags fighting for what remained. */}
          <UserAvatar
            src={member.avatarUrl}
            name={member.name || 'Teacher'}
            size="xl"
            fallbackColor={colors.primary}
          />
        </div>
        <h3 className="font-bold text-lg mb-1" style={{ color: '#222222' }}>{member.name || 'Teacher'}</h3>
        {member.headline && (
          <p className="text-sm text-center mb-2 px-2" style={{ color: '#717171' }}>
            {member.headline}
          </p>
        )}
        {member.specialties && member.specialties.length > 0 && (
          <div className="flex flex-wrap justify-center gap-1.5 mb-3">
            {member.specialties.slice(0, 3).map((specialty, idx) => (
              <span
                key={idx}
                className="px-3 py-1 text-xs font-medium rounded-full"
                style={{ backgroundColor: colors.background, color: colors.primaryDark }}
              >
                {specialty}
              </span>
            ))}
          </div>
        )}
        {/* The bio is not repeated here: it opens with the same sentence as the
            headline, and the full text lives in the profile view this card
            opens. */}
        {member.averageRating && (
          <div className="flex items-center gap-1 mt-2">
            <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            <span className="text-sm font-medium text-gray-700">{member.averageRating}</span>
            <span className="text-xs text-gray-400">({member.reviewsCount === 1 ? (translations?.reviewCountOne || `${member.reviewsCount} review`) : (translations?.reviewCountOther || `${member.reviewsCount} reviews`).replace('%{count}', member.reviewsCount)})</span>
          </div>
        )}
        <div className="flex items-center gap-1 text-xs text-gray-400 group-hover:text-gray-600 transition-colors mt-2">
          <span>{translations?.viewProfile || 'View Profile'}</span>
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </div>
  )
}
